/**
 * One automatic retry for a tray upload that got no answer (B82).
 *
 * The live server stopped in the middle of a photo upload (most likely out of memory) and the photo was lost: the app
 * waited its 30 seconds and showed "Something went wrong on our side", though a new server was up a second later.
 * Now an upload that gets no answer at all (a timeout, a dropped connection, or the platform's 502, 503 or 504 while
 * the server restarts) is sent once more after a short wait. The server's copy check (B61) makes that safe: when the
 * first one did land after all, the retry is answered as a copy of it, and that counts as landed, not as "Already
 * added". A real answer is never retried: a refusal (4xx) says why in its own words, and a 500 is the server's own
 * failure, reported and said as before. A second failure is the batch's failure, as before.
 *
 * Plain TypeScript with no imports, so node --test can run it (trayRetry.b82.test.mjs); the copy check and the wait
 * are handed in by useTrayBatch.
 *
 * Where: useTrayBatch (the Photos page's uploader and every "Take or upload a photo" pop-up).
 *
 * @module trayRetry
 */

/** The platform's answers while the server behind it is down or restarting: no answer from Grow itself. */
const NO_ANSWER_STATUSES: ReadonlySet<number> = new Set([502, 503, 504]);

/** What the one retry is handed. */
export interface RetryOptions {
  /** How long to wait before the retry, in milliseconds. */
  waitMs: number;
  /** Waits that long (setTimeout in the app; recorded in the tests). */
  wait: (ms: number) => Promise<void>;
  /** Whether a failure is the server's copy answer (PhotoCopySkipped). */
  isCopy: (error: unknown) => boolean;
  /** Told of the first failure before the retry, so it still reaches Sentry. */
  onFirstFailure?: (error: unknown) => void;
}

/** How a send that went in ended: the photo the server answered with, or the copy answer to a retry of one that had landed. */
export type RetryResult<T> = { photo: T } | { landedAs: unknown };

/**
 * Whether a failure got no answer from Grow: an Axios error with no response (a timeout or a dropped connection), or
 * one the platform answered with 502, 503 or 504. A request the app cancelled is not.
 *
 * @param error - Whatever the send threw.
 * @returns True when the upload may be sent once more.
 */
export function isNoAnswer(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const failure = error as { isAxiosError?: boolean; code?: string; response?: { status?: number } };
  if (!failure.isAxiosError || failure.code === 'ERR_CANCELED') return false;
  const status = failure.response?.status;
  return status === undefined || NO_ANSWER_STATUSES.has(status);
}

/**
 * Sends an upload, and once more after a short wait when the first send got no answer. A copy answer to the retry
 * means the first send landed: it is handed back as landed. A copy answer to the first send, a real answer, or a
 * second failure is thrown, as without the retry.
 *
 * @param send - The upload.
 * @param options - The wait, the copy check and who is told of the first failure.
 * @returns The photo, or the copy answer to a retry of a photo that had landed.
 * @throws What the send threw, when it is not retried or the retry fails too.
 */
export async function sendWithOneRetry<T>(send: () => Promise<T>, options: RetryOptions): Promise<RetryResult<T>> {
  try {
    return { photo: await send() };
  } catch (error) {
    if (!isNoAnswer(error)) throw error;
    options.onFirstFailure?.(error);
  }
  await options.wait(options.waitMs);
  try {
    return { photo: await send() };
  } catch (error) {
    if (options.isCopy(error)) return { landedAs: error };
    throw error;
  }
}
